"use client";

import { useEffect, useRef } from "react";
import type Centrifuge from "centrifuge";
import { acquireSubscription, releaseSubscription } from "@/src/lib/centrifuge";
import { unwrapCentrifugoPayload } from "@/src/lib/centrifugoPayload";

export type CentrifugoChannelOnEvent = (args: {
  channel: string;
  event: string;
  payload: any;
  raw: any;
  ctx: Centrifuge.PublicationContext;
}) => void;

export type CentrifugoChannelOnPayload = (args: {
  channel: string;
  payload: any;
  raw: any;
  ctx: Centrifuge.PublicationContext;
}) => void;

export interface UseCentrifugoChannelOptions {
  channel: string;
  enabled?: boolean;

  /**
   * If payload contains { event: string }, this is called.
   */
  onEvent?: CentrifugoChannelOnEvent;

  /**
   * Called for any payload (including ones without "event").
   */
  onPayload?: CentrifugoChannelOnPayload;
}

export function useCentrifugoChannel({
  channel,
  enabled = true,
  onEvent,
  onPayload,
}: UseCentrifugoChannelOptions) {
  const subscriptionRef = useRef<Centrifuge.Subscription | null>(null);
  const channelRef = useRef<string>("");

  const onEventRef = useRef<CentrifugoChannelOnEvent | undefined>(onEvent);
  const onPayloadRef = useRef<CentrifugoChannelOnPayload | undefined>(onPayload);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    onPayloadRef.current = onPayload;
  }, [onPayload]);

  useEffect(() => {
    if (!enabled || !channel) return;

    // Reuse existing subscription for the same channel.
    if (subscriptionRef.current && channelRef.current === channel) {
      // Подписка уже существует, не создаем новую
      return;
    }

    // Cleanup previous subscription if switching channels.
    if (subscriptionRef.current && channelRef.current) {
      const prevChannel = channelRef.current;
      releaseSubscription(prevChannel);
      subscriptionRef.current = null;
      channelRef.current = "";
    }

    // Получаем подписку из реестра (увеличиваем refCount)
    const result = acquireSubscription(channel);
    if (!result) return;

    const { subscription, isNew } = result;

    // Устанавливаем обработчики для этого конкретного экземпляра хука
    subscription.on("publish", (ctx: Centrifuge.PublicationContext) => {
      const raw = ctx.data;
      const payload = unwrapCentrifugoPayload(raw);

      try {
        onPayloadRef.current?.({ channel, payload, raw, ctx });
      } catch (e) {
        console.error("[Centrifugo] onPayload handler failed", { channel, e });
      }

      const event =
        payload && typeof payload === "object" && typeof (payload as any).event === "string"
          ? String((payload as any).event)
          : null;

      if (event) {
        try {
          onEventRef.current?.({ channel, event, payload, raw, ctx });
        } catch (e) {
          console.error("[Centrifugo] onEvent handler failed", { channel, event, e });
        }
      }
    });

    // Подписываемся на канал ТОЛЬКО если это новая подписка
    if (isNew) {
      subscription.subscribe();
    }
    
    subscriptionRef.current = subscription;
    channelRef.current = channel;

    return () => {
      // При размонтировании уменьшаем refCount
      if (channelRef.current) {
        releaseSubscription(channelRef.current);
      }
      subscriptionRef.current = null;
      channelRef.current = "";
    };
  }, [channel, enabled]);

  const publish = async (data: any): Promise<boolean> => {
    const sub = subscriptionRef.current;
    if (!sub) return false;

    await sub.publish(data);
    return true;
  };

  /**
   * Кто сейчас держит этот канал открытым. Для канала задания это ровно те, кто
   * находится в экзамене прямо сейчас: студент подписывается на канал, как только
   * открыл задание, — ещё до того, как поднимет камеру.
   */
  const presence = async (): Promise<Record<string, { user?: string | number }>> => {
    const sub = subscriptionRef.current as unknown as {
      presence?: () => Promise<{ presence?: Record<string, { user?: string | number }> }>;
    } | null;

    if (!sub?.presence) return {};

    const data = await sub.presence();
    return data?.presence ?? {};
  };

  return { publish, presence };
}